/**
 * This object can be used when characters need to be added to the next mounted editor, but
 * the editor is not available yet.
 * For example:  The document cards can accept key-presses which will open the card modal
 * and insert those characters into the modal editor.
 */

import { UnknownAction } from 'redux';
import { MiddlewareNextFunction, ReduxStore } from '../../../client/types/reduxTypes';
import { ELEMENT_CREATE } from '../../elements/elementConstants';

let typingBuffer: string = '';

// Track the editorId currently listening to the buffer
let listeningEditorId: string = '';

// The buffer is short-lived by design; if an event isn't received for a short time we clear it
let bufferTimeout: ReturnType<typeof setTimeout> | null = null;
const BUFFER_DURATION = 150;
const clearBufferTimeout = () => {
    if (!bufferTimeout) return;
    clearTimeout(bufferTimeout);
    bufferTimeout = null;
};

// Typing buffer gets automatically reset
// - when consumed
// - when an element starts listening using a different id
const clear = (): void => {
    typingBuffer = '';
    clearBufferTimeout();
};

export const listen = (id: string): void => {
    if (id === listeningEditorId) return;

    // clear the buffer whenever the listening editor changes
    clear();
    listeningEditorId = id;
};

export const addChar = (char: string): void => {
    typingBuffer += char;

    clearBufferTimeout();
    bufferTimeout = setTimeout(clear, BUFFER_DURATION);
};

const processKeyEvent = (event: KeyboardEvent) => {
    // Ignore modifiers
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    // If we're already typing into something editable, skip the keybuffer
    const htmlTarget = event.target as HTMLElement;
    if (htmlTarget.contentEditable === 'true' || htmlTarget.tagName === 'INPUT' || htmlTarget.tagName === 'TEXTAREA') {
        return false;
    }

    // Only buffer if the key event is coming from within an Element
    const element = htmlTarget.closest('.Element');
    if (!element) return false;

    if (event.key === ' ' && typingBuffer === '') {
        // don't buffer a keypress if we'd be starting the buffer with a space
        // (otherwise eg when typing "[] " to start a task, the task's first character would be a space)
        return;
    }

    // Add normal typing to keybuffer
    if (event.key.length === 1) {
        addChar(event.key);
        return;
    }

    // Handle special keypresses
    switch (event.key) {
        case 'Backspace':
            // Unlikely that a user will ever be typing so fast that they can make edits to
            // the buffer before it gets consumed, but, handle it just in case
            typingBuffer = typingBuffer.slice(0, -1);
            break;
    }
};

export const consume = (): string => {
    const chars = typingBuffer;
    clear();
    return chars;
};

export const handleKeyEvent = (id: string, event: KeyboardEvent): void => {
    if (!event.key) return;

    listen(id);
    processKeyEvent(event);
    event.preventDefault();
    event.stopPropagation();
};

export const initListener = () => {
    document.addEventListener('keydown', processKeyEvent);
};

export const typingBufferMiddleware =
    (store: ReduxStore) => (next: MiddlewareNextFunction) => async (action: UnknownAction) => {
        switch (action.type) {
            case ELEMENT_CREATE:
                clear();
                break;
        }
        return next(action);
    };
